<template lang="pug">
include ../Bemto
+b.phone-verification
  +e.step(v-if="!codeSent")
    +b.P.phone-verification__label {{ _('Номер телефону') + '*' }}
    MazPhoneNumberInput(
      :default-country-code="'UA'"
      name='phone'
      v-model='phone'
      :translations="phoneInputTranslation"
      @update="onPhoneUpdate"
    )
    +b.P.phone-verification__error(v-if="phoneError") {{ phoneError }}
    +b.BUTTON.el-btn.--default.text.--color-white.phone-verification__btn(
      @click.prevent="sendCode"
      :disabled="!canSubmitPhone || isSending"
    ) {{ _('Надіслати код') }}

  +e.step(v-else)
    +b.P.phone-verification__label {{ _('Введіть код з SMS') }}
    +b.P.phone-verification__phone-display {{ formattedPhone }}
      +b.SPAN.phone-verification__change(@click="resetPhone") {{ _('Змінити') }}
    +e.code-inputs
      input.phone-verification__code-input(
        v-for="(digit, index) in codeDigits"
        :key="index"
        :ref="'code' + index"
        type="tel"
        maxlength="1"
        v-model="codeDigits[index]"
        :autocomplete="index === 0 ? 'one-time-code' : 'off'"
        :aria-label="_('Цифра') + ' ' + (index + 1)"
        @input="onCodeInput(index)"
        @keydown.delete="onCodeDelete(index)"
        @paste="onCodePaste"
      )
    +b.P.phone-verification__error(v-if="codeError") {{ codeError }}
    +b.P.phone-verification__success(v-if="isVerified") {{ _('Номер підтверджено') }}
    +e.actions
      +b.BUTTON.el-btn.--default.text.--color-white.phone-verification__btn(
        @click.prevent="verifyCode"
        :disabled="fullCode.length !== 6 || isVerifying || isVerified"
      ) {{ isVerified ? _('Підтверджено ✓') : _('Підтвердити') }}
      +b.P.phone-verification__resend(v-if="!isVerified")
        template(v-if="countdown > 0")
          | {{ _('Надіслати повторно через') }} {{ countdown }}{{ _('с') }}
        template(v-else)
          +b.SPAN.phone-verification__resend-link(@click="sendCode") {{ _('Надіслати код повторно') }}
</template>

<script>
import { MazPhoneNumberInput } from 'maz-ui'
import { phoneSendCodeAPI, phoneVerifyCodeAPI } from '@requests/services/services'

export default {
  name: 'phone-verification',

  components: {
    MazPhoneNumberInput,
  },

  props: {
    initialPhone: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      phone: this.initialPhone,
      isPhoneValid: false,
      phoneError: null,
      codeSent: false,
      codeDigits: ['', '', '', '', '', ''],
      codeError: null,
      isVerified: false,
      isSending: false,
      isVerifying: false,
      countdown: 0,
      countdownTimer: null,
      phoneInputTranslation: {
        countrySelectorLabel: this._('Країна'),
        countrySelectorError: this._('Пошук'),
        phoneNumberLabel: this._('Номер телефону'),
        example: this._('Приклад:'),
      },
    }
  },

  computed: {
    fullCode() {
      return this.codeDigits.join('')
    },
    normalizedPhone() {
      const digits = String(this.phone || '').replace(/\D/g, '')
      if (digits.length === 10 && digits.startsWith('0')) {
        return `+380${digits.slice(1)}`
      }
      return this.phone
    },
    canSubmitPhone() {
      if (this.isPhoneValid) return true
      const digits = String(this.phone || '').replace(/\D/g, '')
      return digits.length === 10 && digits.startsWith('0')
    },
    formattedPhone() {
      return this.normalizedPhone || ''
    },
  },

  beforeDestroy() {
    this.clearCountdown()
  },

  methods: {
    onPhoneUpdate(payload) {
      this.isPhoneValid = payload.isValid || false
      this.phoneError = null
    },

    async sendCode() {
      if (this.isSending) return
      this.isSending = true
      this.phoneError = null

      try {
        await phoneSendCodeAPI.execute({}, { phone: this.normalizedPhone })
        this.codeSent = true
        this.codeDigits = ['', '', '', '', '', '']
        this.codeError = null
        this.startCountdown()
        this.$nextTick(() => {
          const ref = this.$refs.code0
          if (ref && ref[0]) {
            ref[0].focus()
          }
        })
      } catch (e) {
        try {
          const body = await e.json()
          this.phoneError = body.errors?.[0]?.state?.phone?.[0]?.message
            || body.error
            || body.detail
            || this._('Помилка надсилання коду')
        } catch (_) {
          this.phoneError = this._('Забагато запитів. Спробуйте пізніше.')
        }
      } finally {
        this.isSending = false
      }
    },

    async verifyCode() {
      if (this.isVerifying || this.fullCode.length !== 6) return
      this.isVerifying = true
      this.codeError = null

      try {
        await phoneVerifyCodeAPI.execute({}, {
          phone: this.normalizedPhone,
          code: this.fullCode,
        })
        this.isVerified = true
        this.$emit('verified', this.phone)
      } catch (e) {
        try {
          const body = await e.json()
          this.codeError = body.errors?.[0]?.state?.code?.[0]?.message
            || body.error
            || body.detail
            || this._('Невірний код')
        } catch (_) {
          this.codeError = this._('Забагато спроб. Спробуйте пізніше.')
        }
      } finally {
        this.isVerifying = false
      }
    },

    resetPhone() {
      this.codeSent = false
      this.isVerified = false
      this.codeError = null
      this.clearCountdown()
    },

    onCodeInput(index) {
      if (this.codeDigits[index] && index < 5) {
        const nextRef = this.$refs['code' + (index + 1)]
        if (nextRef && nextRef[0]) nextRef[0].focus()
      }
      if (this.fullCode.length === 6) {
        this.verifyCode()
      }
    },

    onCodeDelete(index) {
      if (!this.codeDigits[index] && index > 0) {
        const prevRef = this.$refs['code' + (index - 1)]
        if (prevRef && prevRef[0]) prevRef[0].focus()
      }
    },

    onCodePaste(event) {
      const paste = (event.clipboardData || window.clipboardData)
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, 6)
      if (paste.length === 6) {
        event.preventDefault()
        this.codeDigits = paste.split('')
        this.verifyCode()
      }
    },

    startCountdown() {
      this.countdown = 120
      this.clearCountdown()
      this.countdownTimer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          this.clearCountdown()
        }
      }, 1000)
    },

    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
  },
}
</script>

<style>
@import '~maz-ui/lib/css/base.css';
@import '~maz-ui/lib/css/maz-phone-number-input.css';
</style>

<style lang="scss" scoped>
.phone-verification {
  &__label {
    font-family: Open Sans;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  &__error {
    color: #e53e3e;
    font-size: 13px;
    margin-top: 4px;
  }

  &__success {
    color: #5CA300;
    font-size: 13px;
    font-weight: 600;
    margin-top: 4px;
  }

  &__btn {
    margin-top: 12px;
    height: 40px;
    font-family: Open Sans;
    font-size: 14px;
    font-weight: 600;

    &:hover {
      background: #5CA300;
    }
  }

  &__phone-display {
    font-size: 14px;
    margin-bottom: 12px;
  }

  &__change {
    color: #0071c2;
    cursor: pointer;
    margin-left: 8px;
    font-size: 13px;
  }

  &__code-inputs {
    display: flex;
    gap: 8px;
    justify-content: center;
  }

  &__code-input {
    width: 48px;
    height: 48px;
    text-align: center;
    font-size: 20px;
    font-weight: 700;
    border: 2px solid #E2E2E2;
    border-radius: 8px;
    outline: none;

    &:focus {
      border-color: #7069A9;
    }
  }

  &__resend {
    font-size: 13px;
    margin-top: 12px;
    text-align: center;
    color: #666;
  }

  &__resend-link {
    color: #0071c2;
    cursor: pointer;
  }
}
</style>
